import { Link } from '@/components/atoms/Link';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import { getTagHref } from '@/lib/newsTags';
import { CategoryPreview } from '@/services/wordpress/categories';
import clsx from 'clsx';

interface ArticleMetaTagsProps {
  categories: CategoryPreview[];
  tags: string[];
  /** `hero` sits on the dark gradient header, `footer` under the article body. */
  variant?: 'hero' | 'footer';
  /** Max tags shown in the hero; the footer always shows all of them. */
  maxHeroTags?: number;
  className?: string;
}

/** Wraps a pill so the whole row height is tappable on phones (>= 40px). */
const PILL_LINK_CLASS =
  'inline-flex min-h-[40px] items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

/**
 * Category + tag pills for a news article, using the site's existing Badge
 * component (same pills as the /news "All articles" filters and the sidebar
 * "Categories" list). Categories link to /news/category/[slug]; tags link to
 * /news/tag/[slug], which lists other articles with that tag.
 */
export const ArticleMetaTags = ({
  categories,
  tags,
  variant = 'footer',
  maxHeroTags = 4,
  className,
}: ArticleMetaTagsProps) => {
  if (!categories.length && !tags.length) return null;

  if (variant === 'hero') {
    return (
      <div
        className={clsx('flex flex-row flex-wrap gap-x-s gap-y-xs', className)}
      >
        {categories.map((category) => (
          <Link
            href={`/news/category/${category.slug}`}
            key={category.id}
            className={PILL_LINK_CLASS}
          >
            <Badge
              className="cursor-pointer border-[1px] border-white bg-dark-blue-gradient"
              label={category.name}
              variant={BadgeVariants.Dark}
              onClick={() => undefined}
            />
          </Link>
        ))}
        {tags.slice(0, maxHeroTags).map((tag) => (
          <Link href={getTagHref(tag)} key={tag} className={PILL_LINK_CLASS}>
            <Badge
              className="cursor-pointer border-[1px] border-white/30 !bg-white/10 !text-white hover:!bg-white/20"
              label={`#${tag}`}
              onClick={() => undefined}
            />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex flex-col gap-s border-t border-neutral-300 pt-xl',
        className
      )}
    >
      {categories.length > 0 && (
        <div className="flex flex-row flex-wrap items-center gap-x-s gap-y-xs">
          <span className="mr-xs text-body2Regular uppercase text-neutral-600">
            Category
          </span>
          {categories.map((category) => (
            <Link
              href={`/news/category/${category.slug}`}
              key={category.id}
              className={PILL_LINK_CLASS}
            >
              <Badge
                className="cursor-pointer"
                label={category.name}
                variant={BadgeVariants.Blue}
                onClick={() => undefined}
              />
            </Link>
          ))}
        </div>
      )}
      {tags.length > 0 && (
        <div className="flex flex-row flex-wrap items-center gap-x-s gap-y-xs">
          <span className="mr-xs text-body2Regular uppercase text-neutral-600">
            Tags
          </span>
          {tags.map((tag) => (
            <Link href={getTagHref(tag)} key={tag} className={PILL_LINK_CLASS}>
              <Badge
                className="cursor-pointer hover:!bg-neutral-300"
                label={`#${tag}`}
                variant={BadgeVariants.Gray}
                onClick={() => undefined}
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
